import { ArrowLeft, ArrowRight, Plane, TrainFront, Clock, Lightbulb, Stamp } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Reveal } from "@/components/motion";
import { Seo } from "@/components/Seo";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { ShareButton } from "@/components/ShareButton";
import { GygTourBand } from "@/components/GetYourGuide";
import { ITINERARY_3D } from "@/data/planner";

const SITE = "https://cphhighlights.com";

const HERO_IMG =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/yqxf6kyc_2026-10-06%2000_13_25.062%2B0200.webp";

const TIMELINE = [
  {
    time: "0:00",
    title: "Land & head into the city",
    body: "Follow the signs to the Metro — it leaves straight from Terminal 3.",
  },
  {
    time: "0:10",
    title: "Metro to Kongens Nytorv",
    body: "The M2 runs straight to the centre in about 15 minutes. Get off at Kongens Nytorv — it's a five-minute walk to Nyhavn.",
  },
  {
    time: "0:30",
    title: "Nyhavn",
    body: "The postcard: coloured houses, old wooden ships and a canal full of cafés. A stroll along both sides takes about 30 minutes — grab a hotdog from one of the stands by the canal.",
  },
  {
    time: "1:00",
    title: "Canal boat tour",
    body: "The 1 h 15 min harbour loop from Nyhavn is the single best layover move — you see the Opera House, Christianshavn and the Little Mermaid without any walking.",
  },
  {
    time: "2:15",
    title: "Amalienborg & the Marble Church",
    body: "A flat 10-minute walk from Nyhavn brings you to the royal square — the guard change is at noon — and the Marble Church's huge dome is right behind it. Hungry? Grab a pastry from Hart or a quick to-go sandwich from the 7-Eleven near the Marble Church.",
  },
  {
    time: "3:00",
    title: "The Round Tower / Torvehallerne",
    body: "On the way there you pass Købmagergade's shops — worth a browse. Then climb the spiral ramp for the view over the rooftops, or keep going straight to Torvehallerne if you'd rather browse the indoor food market. From here it's a few minutes on to Nørreport station and the Metro.",
  },
  {
    time: "4:00",
    title: "Back to the airport",
    body: "Metro from Nørreport or Kongens Nytorv — 15 minutes, trains every few minutes. You're back around the 4½-hour mark, with margin before the 2-hour security buffer.",
  },
];

const WEBPAGE = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "What to Do on a Copenhagen Layover",
  url: `${SITE}/layover`,
  description:
    "What to do on a Copenhagen layover: the airport is 15 minutes from the city centre. A realistic 6-hour plan, transport included — luggage, Metro, Nyhavn, canal tour, the royal quarter and back in time for your flight.",
  isPartOf: { "@type": "WebSite", name: "CPH Highlights", url: `${SITE}/` },
  publisher: { "@type": "Organization", name: "CPH Highlights", url: `${SITE}/` },
};

const BREADCRUMB = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "What to Do on a Copenhagen Layover", item: `${SITE}/layover` },
  ],
};

const FAQS = [
  {
    q: "Is 6 hours enough for a Copenhagen layover?",
    a: "Yes — the airport is only 15 minutes from the city centre by Metro, so a 6-hour layover gives you about 4½ hours in town with a safe margin. Only have 4–5 hours? Do Nyhavn and the canal boat tour and nothing else.",
  },
  {
    q: "Can I leave the airport during a layover?",
    a: "Denmark is in Schengen, so arriving from another Schengen country you simply walk out. From outside the EU/Schengen you'll clear passport control on the way out and again on the way back in — allow 30–60 minutes extra each way on busy days, and check that your visa allows entry.",
  },
  {
    q: "Where do I leave my luggage on a layover?",
    a: "Luggage lockers sit by Terminal 2/3 at the airport — the easiest option. In the city, Nørreport station also has lockers if you'd rather stash bags downtown.",
    link: { label: "Book luggage storage in advance", href: "https://gyg.me/SBMYL4Z9" },
  },
  {
    q: "How do I get from Copenhagen Airport to the city centre?",
    a: "Metro M2 from Terminal 3 to Kongens Nytorv takes about 15 minutes and puts you three minutes from Nyhavn. The train to the central station is just as fast and better for Vesterbro. A City Pass or single ticket covers both — never take a taxi.",
  },
  {
    q: "What if my layover is at night?",
    a: "The Metro runs 24/7, so you can always get in and back — but after hours the city is quiet and most sights are shut. A night layover is better spent at the airport; in summer, though, the bright Nordic evenings make a harbour stroll genuinely worth it.",
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function LayoverPage() {
  return (
    <main data-testid="layover-page" className="bg-[#F9F8F6] text-black font-body">
      <Seo
        title="What to Do on a Copenhagen Layover"
        description="What to do on a Copenhagen layover — the airport is 15 minutes from the centre by Metro. A realistic 6-hour plan: Nyhavn, a canal tour and the royal quarter."
        path="/layover"
        image="https://images.pexels.com/photos/17603710/pexels-photo-17603710.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(BREADCRUMB)}</script>
        <script type="application/ld+json">{JSON.stringify(WEBPAGE)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>

      {/* hero */}
      <section className="relative bg-[#13141A] text-white overflow-hidden border-b border-zinc-200 pt-16 md:pt-20">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Descending into Copenhagen — the city on the horizon beyond Amager's forests and wetlands"
            className="w-full h-full object-cover object-[center_30%] opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-24">
          <Reveal>
            <div className="flex items-center gap-3 mb-12 md:mb-14">
              <Link
                to="/"
                data-testid="layover-back"
                className="inline-flex items-center gap-2 font-body font-700 text-sm bg-[#DF3226] text-white px-5 py-2.5 rounded-full border border-black shadow-brut hover:-translate-y-0.5 transition-all"
              >
                <ArrowLeft size={16} /> Back to start
              </Link>
              <ShareButton title="What to Do on a Copenhagen Layover" testid="layover-share" />
            </div>
            <p className="font-body font-800 text-xs uppercase tracking-[0.3em] eyebrow text-white mb-4">
              15 MINUTES FROM ARRIVALS TO THE CITY
            </p>
            <h1 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
              Long layover?<br />
              <span className="font-editorial italic font-600">See Copenhagen anyway</span>
            </h1>
            <p className="font-body text-base md:text-lg text-white/80 max-w-2xl mt-6 leading-relaxed">
              Copenhagen Airport sits closer to its city centre than almost any capital airport in
              Europe. With a 6-hour layover you can genuinely see the highlights, transport included —
              here's the timed plan. Shorter or longer? There are options below.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ground rules */}
      <section className="py-12 md:py-16 border-b border-zinc-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            {
              icon: Clock,
              title: "The honest maths",
              body: "The plan below runs about 4½ hours in the city, walking included (the planner counts every minute). Add the Metro both ways and you're back around the 5-hour mark — a realistic 6-hour layover. Be at security 2 hours before an international flight.",
            },
            {
              icon: TrainFront,
              title: "Metro or train, never a taxi",
              body: "Both leave from Terminal 3. Metro M2 to Kongens Nytorv (for Nyhavn) or the train to the central station (for Vesterbro). ~15 minutes, a City Pass or single ticket covers it.",
            },
            {
              icon: Stamp,
              title: "Non-EU passport? Add time",
              body: "Flying in from outside the EU/Schengen? You'll clear passport control on the way out and again on the way back in — on busy days that's 30–60 minutes extra each way. Treat the 6-hour plan as the safe minimum.",
            },
          ].map((c, i) => (
            <Reveal key={c.title} delay={i * 0.05} y={30}>
              <div data-testid={`layover-rule-${i}`} className="h-full bg-[#F9F8F6] border border-zinc-200 shadow-brut rounded-xl p-6">
                <c.icon size={22} className="text-[#DF3226]" />
                <h2 className="font-display font-900 tracking-tight text-xl mt-3">{c.title}</h2>
                <p className="font-body text-sm text-black/70 mt-2 leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* timeline */}
      <section className="py-12 md:py-20">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <Reveal>
            <h2 className="font-display font-900 tracking-tighter leading-[0.95] text-3xl sm:text-4xl lg:text-5xl mb-2">
              The 6-hour layover, <span className="font-editorial italic font-600">timed</span>
            </h2>
            <p className="font-body text-base text-black/60 mb-10">
              Clock starts when you step out of arrivals.
            </p>
          </Reveal>
          <ol className="space-y-0">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.time} delay={i * 0.03} y={24}>
                <li data-testid={`layover-step-${i}`} className="flex gap-5 md:gap-8 group">
                  <div className="flex flex-col items-center">
                    <span className="shrink-0 font-display font-900 text-sm bg-black text-white rounded-full px-3 py-1.5 min-w-[64px] text-center">
                      {t.time}
                    </span>
                    {i < TIMELINE.length - 1 && <span className="w-px flex-1 bg-zinc-300 my-1" />}
                  </div>
                  <div className="pb-10">
                    <h3 className="font-display font-900 tracking-tight text-xl md:text-2xl leading-tight">{t.title}</h3>
                    <p className="font-body text-sm md:text-base text-black/70 mt-2 leading-relaxed max-w-xl">{t.body}</p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>

          <div className="bg-[#FCEFC5] border border-black shadow-brut rounded-xl p-5 md:p-6 flex items-start gap-3 mt-2">
            <Lightbulb size={20} className="shrink-0 mt-0.5 text-[#B8860B]" />
            <p className="font-body text-sm md:text-base text-black/75 leading-relaxed">
              More time? Add a stroll at Torvehallerne — it slots in between the Round Tower and the ride back.
              Only 4–5 hours? Skip everything after the boat: Nyhavn plus the canal tour is the
              greatest-hits version (about 2 hours in the city).
            </p>
          </div>

        </div>
      </section>

      {/* CTA */}
      <section className="bg-black text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-8">
          <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
            Got a full day?<br />
            <span className="font-editorial italic font-600">The extreme day trip</span>
          </h2>
          <Link
            to="/edt"
            data-testid="layover-edt-cta"
            className="inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full border border-white/20 shadow-brut hover:-translate-y-0.5 hover:shadow-xl transition-all"
          >
            See the day trip <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <GygTourBand tourId="920682" label="MAKE THE LAYOVER A TOUR" />
      <ExploreLinks
        testid="explore-layover"
        links={[
          { label: "Tickets & cards — the transport guide", to: "/transport" },
          { label: "Staying longer? The 3-day itinerary", to: "/copenhagen-itinerary" },
          { label: "Raining on your layover? The rainy-day guide", to: "/rainy-day" },
        ]}
      />

      {/* FAQ — last, before the share bar */}
      <section data-testid="layover-faq" className="bg-white border-t border-dotted border-zinc-300">
        <div className="max-w-[900px] mx-auto px-5 md:px-10 py-12 md:py-16">
          <Reveal>
            <h2 className="font-display font-900 tracking-tighter text-3xl md:text-4xl mb-8">
              Layover questions, <span className="font-editorial italic font-600">answered</span>
            </h2>
          </Reveal>
          <div className="divide-y divide-zinc-200 border-y border-dotted border-zinc-300">
            {FAQS.map((f, i) => (
              <details key={f.q} data-testid={`layover-faq-${i}`} className="group py-5">
                <summary className="flex items-center justify-between gap-4 cursor-pointer font-display font-900 text-lg md:text-xl tracking-tight list-none">
                  {f.q}
                  <span className="font-body text-[#DF3226] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 max-w-2xl">
                  {f.a}
                </p>
                {f.link && (
                  <a
                    href={f.link.href}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    data-testid={`layover-faq-link-${i}`}
                    className="group flex items-center gap-2 mt-3 font-body font-700 text-base text-black/60 hover:text-[#DF3226] transition-colors w-fit"
                  >
                    <ArrowRight size={16} className="text-[#DF3226] shrink-0 group-hover:translate-x-1 transition-transform" />
                    {f.link.label}
                  </a>
                )}
              </details>
            ))}
          </div>
        </div>
      </section>
      <ShareBar title="Copenhagen Layover Guide" />
    </main>
  );
}
